/**
 * Voorraad per plek, met een mutatielog (docs/VOORRAAD.md). Alleen voor het beheer: inkoopprijs en
 * salonvoorraad komen nooit in de publieke catalogus (GET /catalog).
 *
 * Plekken:
 * - `webshop`: de voorraad waaruit de webshop verkoopt en verstuurt. Dit is `Product.stock` uit de
 *   catalogus (dezelfde kolom op de server); waar die fysiek ligt maakt voor de data niet uit, dus
 *   een echt magazijn later verandert niets aan de vorm.
 * - `salon`: de voorraad in de salon, voor de kassa.
 * Er komt een plek bij door hem aan `StockLocation` toe te voegen; overal waar per plek gerekend
 * wordt is het een `Record<StockLocation, …>`, zodat de typecheck de nieuwe plek afdwingt.
 */

export type StockLocation = 'webshop' | 'salon'

export const stockLocations: readonly StockLocation[] = ['webshop', 'salon']

/** `retail` = een product uit de catalogus; `verbruik` = professionele verpakking, alleen voor gebruik in de salon. */
export type StockItemKind = 'retail' | 'verbruik'

/** Stand per plek. `null` = niet bijhouden (zoals nu bij de webshop zonder voorraadgetal). Mag negatief: verkocht na een te hoge telling. */
export type StockLevels = Record<StockLocation, number | null>

export type StockItem = {
  /** Bij `retail` het product-id; bij `verbruik` een eigen id ("verbruik-…"). */
  id: string
  kind: StockItemKind
  name: string
  brand?: string
  levels: StockLevels
  /** Inkoopprijs per stuk excl. btw, in centen. Leeg = onbekend (telt dan als 0 in "tegen inkoopprijs"). */
  costPriceCents?: number
  /** Verkocht in de laatste 30 dagen, netto (min correcties en annuleringen). */
  sold30: Record<StockLocation, number>
}

export type StockReason = 'verkoop-webshop' | 'verkoop-salon' | 'verbruik' | 'overboeken' | 'inkoop' | 'afschrijven' | 'telling'

/** `cadeau` = weggegeven bij een bestelling (de server boekt dit zelf voor de cadeaustaffel); `marketing` = samples, influencers, eigen foto's. */
export type WriteOffCause = 'kapot' | 'over-datum' | 'tester' | 'cadeau' | 'marketing' | 'anders'

/** Eén regel in het log: wat er op één plek bij of af ging. Overboeken = twee regels met dezelfde `transferId`. */
export type StockMovement = {
  id: string
  itemId: string
  /** "YYYY-MM-DDTHH:mm", salontijd. */
  at: string
  reason: StockReason
  location: StockLocation
  /** Bij (+) of af (−). Een correctie of annulering van een verkoop is een positieve `verkoop-…`-regel. */
  qty: number
  note?: string
  /** Wie het deed (naam van de beheerder), of "Webshop" bij een bestelling. */
  by: string
  /** Het account dat het deed. Leeg bij de webshop en bij een oudere server. */
  accountId?: string
  /** Bestelnummer of bonnummer. */
  ref?: string
  transferId?: string
  writeOffCause?: WriteOffCause
  /** Inkoopprijs per stuk op het moment van de mutatie (voor "tegen inkoopprijs" in Omzet). */
  costPriceCents?: number
}

/**
 * POST /admin/stock/movements → StockItem (bijgewerkt). Idempotent op `requestId`.
 * Verkopen boekt de server zelf (bestelling, bon); die kunnen hier niet.
 */
export type StockMovementRequest = { requestId: string } & StockMovementInput

/** Wat er geboekt wordt, zonder `requestId`: het formulier bouwt dit, de rekenregels controleren het. */
export type StockMovementInput =
  | ({ type: 'overboeken'; from: StockLocation; to: StockLocation; qty: number } & MovementBase)
  | ({ type: 'verbruik'; location: StockLocation; qty: number } & MovementBase)
  | ({ type: 'inkoop'; location: StockLocation; qty: number; costPriceCents?: number } & MovementBase)
  | ({ type: 'afschrijven'; location: StockLocation; qty: number; cause: WriteOffCause } & MovementBase)
  /** Zet de stand op `counted`; de mutatie is het verschil. Met `expected` (de stand die Sarah zag) → 409 `stock_changed` als er intussen iets verkocht is. */
  | ({ type: 'telling'; location: StockLocation; counted: number; expected: number | null } & MovementBase)

type MovementBase = { itemId: string; note?: string }

export type StockMovementType = StockMovementInput['type']

/** POST /admin/stock/items → StockItem: een verbruiksartikel toevoegen. */
export type NewStockItemRequest = { requestId: string; name: string; brand?: string; costPriceCents?: number }

/** PATCH /admin/stock/items/{id} → StockItem: inkoopprijs (ook van een catalogusproduct). */
export type StockItemPatch = { costPriceCents: number | null }

// ——— Inkoopfactuur ———

/** Eén regel van een inkoopfactuur: wat er binnenkwam, met de inkoopprijs per stuk (excl. btw). */
/**
 * Waar een factuurregel voor is. `winkel` (leeg) telt mee in de gemiddelde inkoopprijs; `salon`
 * (Pro-maten, maskers voor behandelingen) en `sample` (gratis, try-me sizes) niet. Een sample kost € 0.
 */
export type PurchaseUse = 'winkel' | 'salon' | 'sample'
export type PurchaseLineInput = { itemId: string; qty: number; costPriceCents?: number; use?: PurchaseUse }

/**
 * POST /admin/stock/receipts → PurchaseReceipt. Multipart (`file` = de factuur als PDF of foto,
 * `lines` als JSON). Idempotent op `requestId`; 409 `receipt_duplicate` als dezelfde leverancier en
 * hetzelfde factuurnummer al geboekt zijn. Alle regels in één transactie: een `inkoop`-mutatie per
 * regel (met `ref` = factuurnummer), en de inkoopprijs van het artikel wordt bijgewerkt.
 */
export type PurchaseReceiptRequest = {
  requestId: string
  supplier: string
  invoiceNumber: string
  /** "YYYY-MM-DD", niet in de toekomst. */
  invoiceDate: string
  location: StockLocation
  lines: PurchaseLineInput[]
  /** Factuurbedrag excl. btw zoals op papier, om te controleren tegen de regels. */
  invoiceTotalCents?: number
  /** Btw op de factuur (voorbelasting), zoals op papier. Voor de boekhouding (Exact: inkoopboeking). */
  invoiceVatCents?: number
  note?: string
  file?: File
}

export type PurchaseReceipt = {
  id: string
  supplier: string
  invoiceNumber: string
  invoiceDate: string
  location: StockLocation
  lines: (PurchaseLineInput & { name: string })[]
  /** Som van de regels, excl. btw. */
  totalCostCents: number
  invoiceTotalCents?: number
  invoiceVatCents?: number
  note?: string
  /** De bijlage. `url` = tijdelijk te openen (de server geeft een getekende link). */
  file?: { name: string; size: number; type: string; url?: string }
  bookedAt: string
  by: string
  accountId?: string
}

// ——— Telling ———

/** Eén getelde regel; `expected` = de stand die Sarah zag toen ze telde. */
export type StockCountLine = { itemId: string; counted: number; expected: number | null }

/**
 * POST /admin/stock/counts → StockCountResult. Idempotent op `requestId`. Per regel een
 * `telling`-mutatie met het verschil. Is de stand intussen veranderd (verkocht tijdens het tellen),
 * dan wordt die regel niet geboekt maar teruggegeven in `conflicts`: die tel je opnieuw.
 */
export type StockCountRequest = { requestId: string; location: StockLocation; lines: StockCountLine[]; note?: string }

export type StockCount = {
  id: string
  location: StockLocation
  at: string
  by: string
  accountId?: string
  /** Aantal getelde artikelen. */
  counted: number
  /** Alles geteld wat op die plek bijgehouden wordt: telt als de wekelijkse telling. */
  complete: boolean
  differences: number
  /** Netto verschil in stuks, en tegen inkoopprijs (excl. btw). */
  netQty: number
  netCostCents: number
}

export type StockCountConflict = { itemId: string; name: string; expected: number | null; current: number | null }

export type StockCountResult = { count: StockCount; items: StockItem[]; conflicts: StockCountConflict[] }

/** Voorraad in een periode, voor Omzet: aantallen en waarde tegen inkoopprijs (excl. btw). */
export type StockPeriod = Record<'soldWebshop' | 'soldSalon' | 'used' | 'writtenOff', { qty: number; costCents: number }>
